import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Input } from '@/shared/components/ui/input'
import { Plus, RefreshCw, AlertCircle, LayoutGrid, Search } from 'lucide-react'
import { AreasTable } from '../components/AreasTable'
import { AreaFormDialog } from '../components/AreaFormDialog'
import { AreaUsersDialog } from '../components/AreaUsersDialog'
import { AreaGroupsDialog } from '../components/AreaGroupsDialog'
import {
  useAreas,
  useCreateArea,
  useUpdateArea,
  useDeleteArea,
  useToggleAreaActive,
} from '@/modules/users/hooks/useAreas'
import type { AreasFilters, AreaCreateInput, AreaUpdateInput, Area } from '@/modules/users/types/area'
import * as areasApi from '@/modules/users/api/areasApi'

export function AreasPage() {
  useDocumentTitle('Áreas')
  const [filters, setFilters] = useState<AreasFilters>({
    page: 1,
    page_size: 25,
  })
  const [searchValue, setSearchValue] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingArea, setEditingArea] = useState<Area | null>(null)
  const [usersDialogArea, setUsersDialogArea] = useState<{ id: number; name: string } | null>(null)
  const [groupsDialogArea, setGroupsDialogArea] = useState<{ id: number; name: string } | null>(null)

  // Queries
  const { data: areasData, isLoading, refetch } = useAreas(filters)

  // Mutations
  const createArea = useCreateArea()
  const updateArea = useUpdateArea()
  const deleteArea = useDeleteArea()
  const toggleActive = useToggleAreaActive()

  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, page: 1, search: searchValue || undefined }))
  }

  const handleCreateArea = async (data: AreaCreateInput | AreaUpdateInput) => {
    await createArea.mutateAsync(data as AreaCreateInput)
    setIsCreateDialogOpen(false)
  }

  const handleEditClick = async (areaId: number) => {
    try {
      const area = await areasApi.getArea(areaId)
      setEditingArea(area)
      setIsEditDialogOpen(true)
    } catch {
      // Error handled by API client
    }
  }

  const handleUpdateArea = async (data: AreaCreateInput | AreaUpdateInput) => {
    if (!editingArea) return
    await updateArea.mutateAsync({ areaId: editingArea.id, data: data as AreaUpdateInput })
    setIsEditDialogOpen(false)
    setEditingArea(null)
  }

  const handleDelete = async (areaId: number) => {
    if (confirm('¿Está seguro de que desea eliminar esta área?')) {
      await deleteArea.mutateAsync(areaId)
    }
  }

  const handleToggleActive = async (areaId: number) => {
    await toggleActive.mutateAsync(areaId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <LayoutGrid className="h-6 w-6 text-teal-500" />
          Áreas
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setIsCreateDialogOpen(true)} className="bg-teal-800 hover:bg-teal-900 text-white">
            <Plus className="mr-2 h-4 w-4" />
            Nueva Área
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-teal-500/5 border border-teal-500/20 text-sm text-muted-foreground">
        <LayoutGrid className="h-4 w-4 text-teal-500 flex-shrink-0" />
        Las áreas agrupan un laboratorio con sus grupos de permisos. Al asignar un área a un
        usuario, este hereda automáticamente todos los grupos configurados.
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar áreas..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>
          Buscar
        </Button>
        {filters.search && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchValue('')
              setFilters((prev) => ({ ...prev, page: 1, search: undefined }))
            }}
          >
            Limpiar
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : areasData && areasData.results.length > 0 ? (
          <div className="p-6">
            <AreasTable
              areas={areasData.results}
              onEdit={handleEditClick}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              onManageUsers={(id, name) => setUsersDialogArea({ id, name })}
              onManageGroups={(id, name) => setGroupsDialogArea({ id, name })}
            />

            {/* Pagination */}
            {areasData.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(filters.page! - 1) * filters.page_size! + 1} -{' '}
                  {Math.min(filters.page! * filters.page_size!, areasData.count)} de{' '}
                  {areasData.count} áreas
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
                    }
                    disabled={filters.page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">
                    Página {filters.page} de {areasData.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
                    }
                    disabled={filters.page === areasData.total_pages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron áreas</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Crea una nueva área para comenzar a organizar los permisos
            </p>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <AreaFormDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateArea}
        isSubmitting={createArea.isPending}
        mode="create"
      />

      {/* Edit Dialog */}
      <AreaFormDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open)
          if (!open) setEditingArea(null)
        }}
        onSubmit={handleUpdateArea}
        isSubmitting={updateArea.isPending}
        area={editingArea}
        mode="edit"
      />

      {/* Users Dialog */}
      <AreaUsersDialog
        open={!!usersDialogArea}
        onOpenChange={(open) => { if (!open) setUsersDialogArea(null) }}
        areaId={usersDialogArea?.id ?? null}
        areaName={usersDialogArea?.name ?? ''}
      />

      {/* Groups Dialog */}
      <AreaGroupsDialog
        open={!!groupsDialogArea}
        onOpenChange={(open) => { if (!open) setGroupsDialogArea(null) }}
        areaId={groupsDialogArea?.id ?? null}
        areaName={groupsDialogArea?.name ?? ''}
      />
    </div>
  )
}
